import { motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

const VARIETIES = [
  {
    name: "Decongestant",
    tag: "Helps support comfortable breathing*",
    img: "/assets/box-decongestant.webp",
    alt: "Mi Binky Decongestant twin pack — two 15 ml bottles with blue labels in a white windowed box",
    accent: "text-sky-600",
    bg: "from-sky-50 to-white",
    pill: "bg-sky-100 text-sky-700",
  },
  {
    name: "Eucalyptus",
    tag: "Gentle eucalyptus fragrance — helps support easy breathing & comfort*",
    img: "/assets/box-eucalyptus.webp",
    alt: "Mi Binky Eucalyptus twin pack — two 15 ml bottles with green labels in a white windowed box",
    accent: "text-emerald-600",
    bg: "from-emerald-50 to-white",
    pill: "bg-emerald-100 text-emerald-700",
  },
  {
    name: "Chamomile",
    tag: "Gentle chamomile fragrance — helps support relaxation & comfort*",
    img: "/assets/box-chamomile.webp",
    alt: "Mi Binky Chamomile twin pack — two 15 ml bottles with gold labels in a white windowed box",
    accent: "text-amber-600",
    bg: "from-amber-50 to-white",
    pill: "bg-amber-100 text-amber-700",
  },
  {
    name: "Nap & Night-Time",
    tag: "Gentle pine fragrance — helps support a calm, soothing bedtime routine*",
    img: "/assets/box-nighttime.webp",
    alt: "Mi Binky Nap Time twin pack — two 15 ml bottles with purple labels in a white windowed box",
    accent: "text-violet-600",
    bg: "from-violet-50 to-white",
    pill: "bg-violet-100 text-violet-700",
  },
];

const SPECS = ["15 ml", "Soft squeeze bottle", "Narrow dispensing nozzle", "Screw closure"];

export const ComfortRange = () => (
  <section id="range" data-testid="range-section" className="relative overflow-hidden py-24 sm:py-32">
    <div className="pointer-events-none absolute -right-40 top-24 h-[420px] w-[420px] rounded-full bg-binky-pink/10 blur-[110px]" />
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>15 ml Comfort Range</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            The Mi Binky <span className="text-binky-pink">Comfort Range.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
            Four thoughtfully fragranced comfort liquids in the signature BINKY.LIFE 15 ml soft
            squeeze bottle — designed to pair with the absorbent cotton insert inside every Mi
            Binky pacifier.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {SPECS.map((s) => (
              <span
                key={s}
                className="rounded-full border border-binky-blue/15 bg-white px-4 py-1.5 text-xs font-bold text-binky-blue"
              >
                {s}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="relative">
          <motion.img
            src="/assets/multipacks-discovery.webp"
            alt="Mi Binky Discovery Collection — one 15 ml bottle of each of the four varieties in a premium white windowed box"
            loading="lazy"
            data-testid="range-group-image"
            whileHover={{ scale: 1.02, rotate: -0.5 }}
            transition={{ type: "spring", stiffness: 200, damping: 18 }}
            className="mx-auto h-auto w-full max-w-[480px] rounded-[32px] border border-slate-100 bg-white object-contain shadow-2xl shadow-binky-blue/15"
          />
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {VARIETIES.map((v, i) => (
          <Reveal
            key={v.name}
            delay={i * 0.08}
            className={`group flex h-full flex-col overflow-hidden rounded-[28px] border border-slate-100 bg-gradient-to-b ${v.bg} shadow-[0_20px_50px_-24px_rgba(27,79,160,0.25)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_32px_64px_-24px_rgba(240,62,155,0.3)]`}
            data-testid={`range-card-${v.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <div className="flex h-64 items-center justify-center overflow-hidden p-4">
              <img
                src={v.img}
                alt={v.alt}
                loading="lazy"
                className="max-h-full w-auto rounded-xl object-contain drop-shadow-xl transition-transform duration-700 group-hover:scale-[1.04]"
              />
            </div>
            <div className="flex flex-1 flex-col p-6">
              <div className="flex items-center justify-between gap-2">
                <h3 className={`font-display text-lg font-bold ${v.accent}`}>{v.name}</h3>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${v.pill}`}>
                  15 ml
                </span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.tag}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-8">
        <p className="text-xs leading-relaxed text-slate-400" data-testid="range-disclaimer">
          *Comfort fragrance accessory for use with BINKY.LIFE comfort pacifiers only — not a
          medicine. Final product wording will reflect completed independent testing.
        </p>
      </Reveal>
    </div>
  </section>
);
